import React, { useState } from 'react';
import { Copy, Check, Calendar, AlertCircle } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';

const VoucherCard = ({ voucher, application }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator?.clipboard?.writeText(voucher?.voucher_code);
      setCopied(true);
      
      // Haptic feedback for mobile devices
      if (navigator?.vibrate) {
        navigator?.vibrate(50);
      }
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusColor = () => {
    switch (voucher?.voucher_status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'used':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = () => {
    switch (voucher?.voucher_status) {
      case 'active':
        return 'Aktiv';
      case 'used':
        return 'Verwendet';
      case 'expired':
        return 'Abgelaufen';
      case 'cancelled':
        return 'Storniert';
      default:
        return 'Unbekannt';
    }
  };

  const daysUntilExpiry = voucher?.expiry_date 
    ? differenceInDays(new Date(voucher?.expiry_date), new Date())
    : null;

  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Card Header */}
      <div className={`p-4 ${voucher?.voucher_status === 'active' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gray-100'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`font-bold text-lg ${voucher?.voucher_status === 'active' ? 'text-white' : 'text-gray-700'}`}>
            THG-Gutscheincode
          </h3>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor()}`}>
            {getStatusLabel()}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6 space-y-4">
        {/* Voucher Code */}
        <div>
          <label className="text-sm font-medium text-gray-600 mb-2 block">
            Gutscheincode
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 font-mono text-lg font-bold text-gray-900 tracking-wider">
              {voucher?.voucher_code}
            </div>
            <button
              onClick={handleCopyCode}
              className={`p-3 rounded-lg transition-all ${
                copied 
                  ? 'bg-green-500 text-white' :'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title="Code kopieren"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          {copied && (
            <p className="text-sm text-green-600 mt-2 font-medium">
              ✓ Code erfolgreich kopiert!
            </p>
          )}
        </div>

        {/* Vehicle Info */}
        {application && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-1">Fahrzeug</p>
            <p className="text-blue-800 font-semibold">{application?.vehicle_model}</p>
            {application?.chassis_number && (
              <p className="text-xs text-blue-600 mt-1">
                FIN: {application?.chassis_number}
              </p>
            )}
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              Ausgestellt
            </label>
            <p className="text-gray-900 font-semibold">
              {voucher?.issue_date 
                ? format(new Date(voucher?.issue_date), 'dd.MM.yyyy', { locale: de })
                : 'Nicht angegeben'
              }
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              Gültig bis
            </label>
            <p className={`font-semibold ${isExpiringSoon ? 'text-orange-600' : isExpired ? 'text-red-600' : 'text-gray-900'}`}>
              {voucher?.expiry_date 
                ? format(new Date(voucher?.expiry_date), 'dd.MM.yyyy', { locale: de })
                : 'Nicht angegeben'
              }
            </p>
          </div>
        </div>

        {/* Expiry Warning */}
        {isExpiringSoon && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-orange-800">Bald ablaufend</p>
              <p className="text-xs text-orange-700 mt-1">
                Dieser Gutschein läuft in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'Tag' : 'Tagen'} ab
              </p>
            </div>
          </div>
        )}

        {/* Verification Info */}
        {voucher?.verification_count > 0 && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Verifizierungen:</span>
              <span className="font-semibold text-gray-900">{voucher?.verification_count}x</span>
            </div>
            {voucher?.verification_date && (
              <p className="text-xs text-gray-500 mt-1">
                Zuletzt verifiziert: {format(new Date(voucher?.verification_date), 'dd.MM.yyyy HH:mm', { locale: de })}
              </p>
            )}
          </div>
        )}

        {/* Usage Instructions */}
        {voucher?.voucher_status === 'active' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Verwendungshinweise</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Verwenden Sie diesen Code beim Partner zur Einlösung</li>
              <li>• Der Code ist nur einmal verwendbar</li>
              <li>• Bewahren Sie den Code sicher auf</li>
              <li>• Bei Fragen kontaktieren Sie den Partner</li>
            </ul>
          </div>
        )}

        {/* Notes */}
        {voucher?.notes && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-700 mb-1">Hinweise</p>
            <p className="text-sm text-gray-600">{voucher?.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherCard;